import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconHandGrab, IconMinus, IconPlus, IconRefresh } from '@tabler/icons-react';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { renderPdfPageThumbnails } from '../../pdf/shared/pdfPreviewEngine';

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.25;
const DEFAULT_ZOOM = 0.6;

function PreviewToolbar({
    zoom,
    panMode,
    onZoomIn,
    onZoomOut,
    onTogglePan,
    onReset,
    labels,
}) {
    const btnClass = (active = false) => `flex h-8 w-8 items-center justify-center rounded-lg border transition-colors ${
        active
            ? 'border-pdf-cat-solid bg-pdf-cat-tint text-pdf-cat-solid dark:border-orange-500 dark:bg-orange-950/30 dark:text-orange-400'
            : 'border-[#ECE8E4] bg-white text-[#3D3833] hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200'
    }`;

    return (
        <div
            className="ml-auto flex flex-none items-center gap-1 rounded-xl border border-[#ECE8E4] bg-white/95 p-1 shadow-[0_2px_8px_rgba(28,24,21,0.08)] backdrop-blur-sm dark:border-zinc-700 dark:bg-zinc-900/95"
            onMouseDown={(e) => e.stopPropagation()}
        >
            <button
                type="button"
                onClick={onZoomIn}
                disabled={zoom >= ZOOM_MAX}
                className={`${btnClass()} disabled:cursor-not-allowed disabled:opacity-40`}
                aria-label={labels.zoomIn}
            >
                <IconPlus size={16} stroke={2} />
            </button>
            <button
                type="button"
                onClick={onZoomOut}
                disabled={zoom <= ZOOM_MIN}
                className={`${btnClass()} disabled:cursor-not-allowed disabled:opacity-40`}
                aria-label={labels.zoomOut}
            >
                <IconMinus size={16} stroke={2} />
            </button>
            <button
                type="button"
                onClick={onTogglePan}
                className={btnClass(panMode)}
                aria-label={labels.pan}
                aria-pressed={panMode}
            >
                <IconHandGrab size={16} stroke={1.8} />
            </button>
            <button
                type="button"
                onClick={onReset}
                className={btnClass()}
                aria-label={labels.reset}
            >
                <IconRefresh size={16} stroke={1.8} />
            </button>
        </div>
    );
}

/**
 * Vertical, scrollable PDF page preview (canvas thumbnails via pdf.js).
 * Pages scale to the column width so one page is readable at a time.
 */
export default function PdfDocPreview({
    source,
    file,
    blob,
    files,
    resultBlobs,
    activeIndex: controlledIndex,
    onActiveIndexChange,
    maxPages = 40,
    defaultZoom = DEFAULT_ZOOM,
}) {
    const { t } = useTranslation();
    const fileList = files ?? [];
    const [internalIndex, setInternalIndex] = useState(0);
    const activeIndex = controlledIndex ?? internalIndex;
    const setActiveIndex = onActiveIndexChange ?? setInternalIndex;

    const filesKey = useMemo(
        () => fileList.map((f) => `${f.name}-${f.size}-${f.lastModified}`).join('|'),
        [fileList],
    );

    useEffect(() => {
        setActiveIndex(0);
    }, [filesKey, setActiveIndex]);

    const safeIndex = fileList.length > 0
        ? Math.min(activeIndex, fileList.length - 1)
        : 0;

    useEffect(() => {
        if (fileList.length > 0 && activeIndex >= fileList.length) {
            setActiveIndex(fileList.length - 1);
        }
    }, [fileList.length, activeIndex, setActiveIndex]);

    const pdfSource = fileList.length > 0
        ? (resultBlobs?.[safeIndex] ?? fileList[safeIndex])
        : (source ?? blob ?? file);
    const containerRef = useRef(null);
    const scrollRef = useRef(null);
    const dragRef = useRef(null);

    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalPages, setTotalPages] = useState(0);
    const [pageWidth, setPageWidth] = useState(0);
    const [zoom, setZoom] = useState(defaultZoom);
    const [panMode, setPanMode] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const sourceKey = useMemo(() => {
        if (!pdfSource) return null;
        if (pdfSource instanceof File) {
            return `${pdfSource.name}-${pdfSource.size}-${pdfSource.lastModified}`;
        }
        return `${pdfSource.size}-${pdfSource.type}`;
    }, [pdfSource]);

    const toolbarLabels = useMemo(() => ({
        zoomIn: t('toolPage.previewZoomIn'),
        zoomOut: t('toolPage.previewZoomOut'),
        pan: t('toolPage.previewPan'),
        reset: t('toolPage.previewReset'),
    }), [t]);

    const resetView = useCallback(() => {
        setZoom(defaultZoom);
        setPanMode(false);
        setIsDragging(false);
        if (scrollRef.current) {
            scrollRef.current.scrollLeft = 0;
            scrollRef.current.scrollTop = 0;
        }
    }, [defaultZoom]);

    useEffect(() => {
        resetView();
    }, [sourceKey, safeIndex, resetView]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;

        const measure = () => {
            setPageWidth(Math.max(200, Math.floor(el.clientWidth)));
        };

        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [pdfSource, sourceKey]);

    useEffect(() => {
        if (!pdfSource || !sourceKey || pageWidth < 200) {
            setPages([]);
            setTotalPages(0);
            return undefined;
        }

        let cancelled = false;
        setLoading(true);
        const buildingPages = [];

        renderPdfPageThumbnails(pdfSource, {
            maxPages,
            thumbWidth: pageWidth,
            onPage: (page) => {
                if (cancelled) return;
                setTotalPages(page.total);
                if (!buildingPages.some((p) => p.pageNumber === page.pageNumber)) {
                    buildingPages.push(page);
                }
                setPages([...buildingPages].sort((a, b) => a.pageNumber - b.pageNumber));
            },
        })
            .catch(() => {
                if (!cancelled) {
                    setPages([]);
                    setTotalPages(0);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [pdfSource, sourceKey, maxPages, pageWidth]);

    const handlePanStart = (e) => {
        if (!panMode || !scrollRef.current) return;
        if (e.button !== 0) return;
        e.preventDefault();
        setIsDragging(true);
        dragRef.current = {
            x: e.clientX,
            y: e.clientY,
            scrollLeft: scrollRef.current.scrollLeft,
            scrollTop: scrollRef.current.scrollTop,
        };
    };

    useEffect(() => {
        if (!isDragging) return undefined;

        const onMove = (e) => {
            if (!scrollRef.current || !dragRef.current) return;
            e.preventDefault();
            const dx = e.clientX - dragRef.current.x;
            const dy = e.clientY - dragRef.current.y;
            scrollRef.current.scrollLeft = dragRef.current.scrollLeft - dx;
            scrollRef.current.scrollTop = dragRef.current.scrollTop - dy;
        };

        const onEnd = () => {
            setIsDragging(false);
            dragRef.current = null;
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onEnd);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onEnd);
        };
    }, [isDragging]);

    if (!pdfSource) return null;

    const truncated = totalPages > pages.length;
    const scrollClass = panMode
        ? isDragging ? 'cursor-grabbing select-none' : 'cursor-grab'
        : '';

    return (
        <div ref={containerRef} className="relative flex h-full min-h-0 w-full max-w-full flex-col overflow-hidden self-stretch">
            {pdfSource && (pages.length > 0 || loading) ? (
                <div className="flex shrink-0 items-center justify-end gap-2 pb-2">
                    <PreviewToolbar
                        zoom={zoom}
                        panMode={panMode}
                        onZoomIn={() => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)))}
                        onZoomOut={() => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)))}
                        onTogglePan={() => setPanMode((v) => !v)}
                        onReset={resetView}
                        labels={toolbarLabels}
                    />
                </div>
            ) : null}

            {loading && pages.length === 0 ? (
                <div className="flex flex-1 items-center justify-center">
                    <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-primary-100 border-t-primary dark:border-primary-900 dark:border-t-primary" />
                </div>
            ) : pages.length > 0 ? (
                <>
                    <div
                        ref={scrollRef}
                        className={`min-h-0 flex-1 overflow-auto px-1 py-1 [scrollbar-width:thin] ${scrollClass}`}
                        onMouseDown={handlePanStart}
                    >
                        <div
                            className="mx-auto flex flex-col gap-5"
                            style={{
                                width: pageWidth > 0
                                    ? Math.round(pageWidth * zoom)
                                    : `${zoom * 100}%`,
                            }}
                        >
                            {pages.map((page) => (
                                <div key={page.pageNumber} className="w-full flex-none">
                                    <div className="overflow-hidden rounded-xl border border-[#ECE8E4] bg-white shadow-[0_4px_12px_rgba(28,24,21,0.08)] dark:border-zinc-700 dark:bg-zinc-900">
                                        <img
                                            src={page.src}
                                            alt=""
                                            className="mx-auto block h-auto w-full bg-white"
                                            draggable={false}
                                        />
                                    </div>
                                    <p className="mt-2 text-center text-[12px] font-medium text-[#A8A099] dark:text-zinc-500">
                                        {t('toolPage.pageNumber', { number: toBnDigits(page.pageNumber) })}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                    {truncated ? (
                        <p className="mt-2 shrink-0 text-center text-[11px] text-[#A8A099] dark:text-zinc-500">
                            {t('toolPage.pagePreviewTruncated', {
                                shown: toBnDigits(pages.length),
                                total: toBnDigits(totalPages),
                            })}
                        </p>
                    ) : null}
                </>
            ) : null}
        </div>
    );
}

export function PdfMergeStackPreview({ files }) {
    if (!files?.length) return null;

    return (
        <div className="flex items-end justify-center">
            {files.slice(0, 5).map((file, i) => (
                <div
                    key={file.id || file.name || i}
                    className="relative h-[112px] w-[86px] flex-none animate-[toolSlide_0.4s_cubic-bezier(0.2,0.8,0.25,1)_both] rounded-lg border border-[#ECE8E4] bg-white shadow-[0_6px_16px_rgba(28,24,21,0.1)] dark:border-zinc-700 dark:bg-zinc-900"
                    style={{
                        marginLeft: i === 0 ? 0 : -26,
                        transform: `rotate(${i % 2 ? 2.5 : -2.5}deg)`,
                        zIndex: i,
                        animationDelay: `${i * 70}ms`,
                    }}
                >
                    <div className="mx-auto mt-[11px] h-1.5 w-[60%] rounded-sm bg-[#ECE8E4] dark:bg-zinc-700" />
                    <div className="mx-auto mt-[7px] h-[5px] w-[74%] rounded-sm bg-[#F0ECE7] dark:bg-zinc-800" />
                    <div className="mx-auto mt-1.5 h-[5px] w-[50%] rounded-sm bg-[#F0ECE7] dark:bg-zinc-800" />
                    <div className="absolute bottom-[7px] left-0 right-0 truncate px-1 text-center font-mono text-[9px] text-[#A8A099] dark:text-zinc-500">
                        {(file.name || '').replace(/\.pdf$/i, '')}
                    </div>
                </div>
            ))}
        </div>
    );
}
